import { SeeqNames } from '@/main/app.constants.seeqnames';
import type { MarkdownItemOutputV1 } from '@/sdk/model/MarkdownItemOutputV1';
import type { WorksheetOutputV1 } from '@/sdk/model/WorksheetOutputV1';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';

export type FontSize = 'small' | 'medium' | 'large';

export type FormulaAssistantVariant = 'debug' | 'explain';

// Agent builder display info for chat view in agent builder mode
export type AgentBuilderDisplayInfo = {
  name: string;
  icon?: string;
  color?: string;
};

export type ChatData = {
  messages: ChatMessage[];
  origin?: AssistantOrigin;
} & Record<string, any>;

export type ThinkingSectionStatus = 'running' | 'completed' | 'error' | 'unknown';

// New types for thinking sections
export type ThinkingSectionItemType = 'tool_call' | 'code_block' | 'context_retrieval' | 'reasoning_summary';

// Memory types
export interface Memory {
  id: string;
  content: string;
  timestamp: number;
  status: 'pending' | 'saved' | 'discarded';
  updateType?: 'new' | 'update'; // Track if this is a new memory or an update to existing
  previousContent?: string; // Store previous content for updates
}

// Memory event payload types for better type safety
export interface MemoryCreatedPayload {
  content: string;
  memory_id: string;
  is_user_request: boolean;
}

export interface MemoryUpdatedPayload {
  content: string;
  memory_id: string;
  previous_content?: string;
  is_user_request: boolean;
}
export interface ThinkingSectionItem {
  item_id: string; // Primary identifier for items - required
  type?: ThinkingSectionItemType; // Optional - can be empty for placeholders, filled in later
  name?: string; // Optional - can be empty for placeholders, filled in later
  description?: string; // Optional - can be empty for placeholders, filled in later
  content?: string; // For code blocks or detailed content
  status: ThinkingSectionStatus;
  startTime?: number;
  endTime?: number;
  duration?: number; // Pre-calculated duration in milliseconds
  metadata?: Record<string, any>; // For additional tool-specific data
}

export interface ThinkingSection {
  id: string;
  title: string;
  status: ThinkingSectionStatus;
  items: ThinkingSectionItem[];
  startTime: number;
  endTime?: number;
  duration?: number; // Pre-calculated duration in milliseconds
  isCollapsed?: boolean;
}

export type AttachmentType = 'image' | 'csv';

export type ChatAttachment = {
  id: string;
  url: string;
  type: AttachmentType;
  mimeType: string;
  name?: string;
  size?: number;
  csvPreview?: string; // First ~20 rows of CSV content for preview purposes
};

export type ChatMessage = {
  role: 'bot' | 'user';
  dialog: string;
  id: string;
  chatId?: string;
  agentType?: string;
  workflowId?: string;
  workflowName?: string;
  last?: boolean;
  agent?: string;
  origin?: AssistantOrigin;
  originDetails?: AssistantOriginDetails;
  stages?: Record<string, string | string[] | object>;
  components?: string[];
  prompt_classification?: string;
  selectedDocument?: SelectedDocument;
  thinkingSections?: ThinkingSection[]; // New field for thinking sections
  memories?: Memory[]; // New field for memories
  attachments?: ChatAttachment[]; // New field for image attachments
  imageUrl?: string;
  imageAlt?: string;
  imageName?: string;
  attachmentId?: string;
  attachmentUrl?: string;
  versionLabel?: string;
};

export const CONTEXT_BUTTON_AGENTS = ['aq'];

export type AgentType = 'formula' | 'general' | 'code' | 'actions' | 'aq' | 'tome';
export type ReasoningStyle = 'enabled' | 'disabled';

export const assistantLocations = {
  homescreen: SeeqNames.Origins.HomeScreen,
  workbench: SeeqNames.Origins.Workbench,
  organizer: SeeqNames.Origins.Organizer,
  vantage: SeeqNames.Consumption.Vantage,
  datalab: SeeqNames.Origins.DataLab,
  admin: SeeqNames.Origins.Administration,
  action_board: SeeqNames.Origins.ActionBoard,
} as const;

export type AssistantOrigin = keyof typeof assistantLocations | typeof SeeqNames.Origins.LogTracker;
export type AssistantOriginDetails = { label: string; url: string };

/**
 * DO NOT ACCESS THIS DIRECTLY. Use `genAIAgents()`  or getDataLabAiAgents() instead.
 */
export const agents: Agent[] = [
  {
    name: 'General',
    color: '#39516b',
    darkColor: '#54749a',
    key: 'general',
    icon: 'fc-genai-chat',
    description: 'AI_ASSISTANT.AGENT_INTRO_GENERAL',
  },
  {
    name: 'Formula',
    color: '#427c63',
    key: 'formula',
    icon: 'fc-formula-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_FORMULA',
  },
  {
    name: 'Data Lab',
    color: '#f26722',
    key: 'code',
    icon: 'fc-datalab-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_DATA_LAB',
  },
  {
    name: 'Actions',
    color: '#0491c2',
    key: 'actions',
    icon: 'fc-workbook-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_ACTIONS',
  },
  {
    name: 'Agent Q',
    color: '#ff2249',
    key: 'aq',
    icon: 'fc-workbook-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_VANTAGE',
  },
  {
    name: 'Document',
    color: '#4f596b',
    key: 'tome',
    icon: 'fa-book',
    description: 'AI_ASSISTANT.AGENT_INTRO_TOME',
    commandName: 'tome',
  },
];

export interface Agent {
  name: string;
  color: string;
  darkColor?: string;
  key: AgentType;
  icon: string;
  description: string;
  commandName?: string;
}

export type LevelOfSatisfaction = 'notAtAll' | 'notReally' | 'neutral' | 'satisfied' | 'verySatisfied';
type ProblemType =
  | 'wrong'
  | 'tooComplex'
  | 'other'
  | 'unclear'
  | 'concise'
  | 'accurate'
  | 'detailed'
  | 'moreConcise'
  | 'moreDetail'
  | 'moreAccuracy'
  | 'examples';
export type AnswerType = {
  problemKey: ProblemType;
  displayKey: string;
  correctionSuggestion?: string;
  correctionPrompt?: string;
  showInput?: boolean;
};
export type SatisfactionEntry = {
  icon: string;
  satisfaction: LevelOfSatisfaction;
  title: string;
  text?: string;
  options?: AnswerType[];
};

// To ADD feature flag for this
export const getDataLabAiAgents = () => {
  return 'general,formula,code';
};

export interface HistoryEntryType {
  chatId: string;
  origin?: AssistantOrigin;
  name: string;
  sharedBy?: string;
  shared: boolean;
  updatedAt?: string;
  favorite: boolean;
}

export interface GroupedHistoryEntryType {
  favorites: HistoryEntryType[];
  shared_with_me: HistoryEntryType[];
  today: HistoryEntryType[];
  yesterday: HistoryEntryType[];
  this_week: HistoryEntryType[];
  last_week: HistoryEntryType[];
  this_month: HistoryEntryType[];
  last_month: HistoryEntryType[];
  older: HistoryEntryType[];
}

export enum MagiqPromptType {
  IMPACT_REPORT_WORKBENCH = 'impact_report_workbench',
  IMPACT_REPORT_ORGANIZER = 'impact_report_organizer',
  IMPACT_REPORT_VANTAGE = 'impact_report_vantage',
}

export enum PromptCategory {
  FindAndCleanseData = 'Find & Cleanse Data',
  EventsAndPeriodsOfInterest = 'Events & Periods of Interest',
  AnalyzeAndQuantify = 'Analyze & Quantify',
  VisualizeAndGraph = 'Visualize & Graph',
  ModelAndPredict = 'Model & Predict',
  ScaleAnalytics = 'Scale Analytics',
  DocumentAndShare = 'Document & Share',
  ScriptAndCode = 'Script & Code',
  AdminAndDataManagement = 'Admin & Data Management',
  ReferenceYourDocs = 'Reference Your Docs',
  SeeqUseCase = 'AI_ASSISTANT.PROMPT_LIBRARY.CATEGORIES.SEEQ_USE_CASE',
  MyUseCase = 'AI_ASSISTANT.PROMPT_LIBRARY.CATEGORIES.MY_USE_CASE',
  SharedUseCase = 'AI_ASSISTANT.PROMPT_LIBRARY.CATEGORIES.SHARED_WITH_ME',
}

export enum LibraryPurpose {
  UseCase = 'Use Case',
  Prompt = 'Prompt',
}

export interface PromptEntry {
  title: string;
  category: PromptCategory;
  prompt: string;
  agentType: AgentType;
}

// Extended interface based on MarkdownItemOutputV1 with additional prompt-specific fields
export interface LibraryItem extends MarkdownItemOutputV1 {
  agentType: AgentType;
  category: PromptCategory;
  prompt: string;
  purpose: LibraryPurpose;
}

export interface DocumentItem {
  id: string;
  type: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isPinned: boolean;
  owner: {
    id: string;
    name: string;
    type: string;
    username: string;
    email: string;
  };
  effectivePermissions: {
    read: boolean;
    write: boolean;
    manage: boolean;
  };
  isArchived: boolean;
}

export interface DocumentsResponse {
  content: DocumentItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface WorksheetsResponse {
  worksheets: WorksheetOutputV1[];
  offset: number;
  limit: number;
}

export interface DocumentWithWorksheets extends DocumentItem {
  worksheets?: WorksheetOutputV1[];
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface SelectedDocument {
  id: string;
  name: string;
  type: string;
  worksheetId?: string;
  worksheetName?: string;
  parentId?: string; // For worksheet's parent document
}

export interface AIMemory {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface MemoriesResponse {
  approved: AIMemory[];
  pending: AIMemory[];
}

// Document type priority for sorting
export const DOCUMENT_TYPE_PRIORITY: Record<string, number> = {
  Topic: 1,
  Vantage: 2,
  Analysis: 3,
};

/**
 * Supported image types for upload
 */
export const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

/**
 * Supported CSV type for upload
 */
export const SUPPORTED_CSV_TYPE = 'text/csv';

/**
 * Maximum file size for image uploads (10MB)
 */
export const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Maximum file size for CSV uploads (10MB)
 */
export const MAX_CSV_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Maximum width for image uploads (1920px)
 */
export const MAX_WIDTH = 1920;
/**
 * Maximum height for image uploads (1080px)
 */
export const MAX_HEIGHT = 1080;
/**
 * Scale step for image uploads (0.85)
 */
export const SCALE_STEP = 0.85;

/**
 * Maximum number of images allowed per message
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/**
 * Response interface for file upload (images and CSV)
 */
export interface FileUploadResponse {
  id: string;
  url: string;
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface DataLabContext {
  activeFileName?: string;
  activeFileType?: string;
  activeFileContent?: string | Record<string, any>;
  activeCellContent?: string;
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface VantageContext {
  timeRange: string;
  gridState: any;
  selection: SimplifiedNode[];
  quickFilter: string;
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface WorkstepContext {
  id: string;
  createdAt: number;
  version: number;
  state: any;
}

/**
 * Replicated in genai/app/utils/types.py
 * Do not make changes without also porting them to that file
 */
export interface ChatContext {
  workstep?: WorkstepContext;
  selectedDocument?: SelectedDocument | SelectedDocument[];
  dataLab?: DataLabContext;
  timezone?: string;
  vantage?: {
    timeRange: string;
    gridState: any;
    selection: SimplifiedNode[];
    quickFilter: string;
  };
}

export interface MigrateUserChatsResponse {
  message: string;
  migrated: number;
  failed: number;
  orphans: number;
  orphans_removed: number;
}
